import React, { useEffect, useRef, useState } from "react";
import { Bell, Sparkles, Radio, Coins, Swords } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";

/**
 * PlyxNotificationBell — polled (every 30s) in-app notification surface.
 *
 * Shows an unread-count badge on the bell icon; clicking opens a small
 * dropdown of the 10 most-recent notifications. Mini Hour activations
 * + "creator goes live" pings appear here so followers can jump back
 * into a creator's stream during the 5-minute 2× window. Hidden for
 * logged-out users.
 *
 * Also fires a one-time `sonner` toast for each freshly-unread notif
 * on next pageview (tracked via localStorage so it never double-fires
 * across tabs/refreshes). The toast deep-links to the same `link` as
 * the dropdown entry — giving creators a pull-back-in surface even
 * when the user never opens the bell.
 */
const TOAST_SEEN_KEY = "plyx_notif_toasted_ids";
const TOAST_SEEN_MAX = 200;

/**
 * PlyxNotificationBell — polled (every 30s) in-app notification surface.
 *
 * Shows an unread-count badge on the bell icon; clicking opens a small
 * dropdown of the 10 most-recent notifications. Mini Hour activations
 * appear here so followers can jump back into a creator's stream during
 * the 5-minute 2× window. Hidden for logged-out users.
 */
const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

// Read + write the small set of notification ids we've already toasted.
// localStorage so toasts don't double-fire across page reloads in the
// same tab; sessionStorage would lose them on refresh and re-spam.
const readToastedIds = () => {
  try {
    const raw = localStorage.getItem(TOAST_SEEN_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
};

const writeToastedIds = (ids) => {
  try {
    // Cap the set so localStorage doesn't grow unbounded — keep the most
    // recent N entries (Sets preserve insertion order in modern JS).
    const arr = Array.from(ids).slice(-TOAST_SEEN_MAX);
    localStorage.setItem(TOAST_SEEN_KEY, JSON.stringify(arr));
  } catch { /* private mode / quota — silent */ }
};

// Pick a sensible icon per notif type so the toast feels native.
const iconFor = (type) => {
  if (type && type.startsWith("millions.")) return <Swords className="w-4 h-4 text-[#FFB42D]" />;
  if (type === "mini_hour.start") return <Sparkles className="w-4 h-4 text-[#FFCC2E]" />;
  if (type === "live.start") return <Radio className="w-4 h-4 text-[#FF3D7F]" />;
  if (type === "live.tip") return <Coins className="w-4 h-4 text-[#FFCC2E]" />;
  return <Bell className="w-4 h-4 text-white/70" />;
};

const PlyxNotificationBell = () => {
  const { user } = usePlyxAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setItems([]); setUnread(0);
      return;
    }
    let mounted = true;
    let firstLoad = true;
    const load = () =>
      api.get("/plyx/me/notifications?limit=10")
        .then((r) => {
          if (!mounted) return;
          const fresh = r.data?.notifications || [];
          setItems(fresh);
          setUnread(r.data?.unread_count || 0);
          // One-time toast surfacing — only after first load so the user
          // doesn't get bombed with toasts for every old unread on a
          // fresh tab. After first load we still toast newly-arrived
          // unread items (which is the desired pull-back-in moment).
          if (!firstLoad) {
            const seen = readToastedIds();
            const toToast = fresh.filter(
              (n) => !n.is_read && n.notification_id && !seen.has(n.notification_id)
            ).slice(0, 3); // safety cap — never more than 3 toasts per poll
            toToast.forEach((n) => {
              toast.custom(
                (id) => (
                  <a
                    href={n.link || "#"}
                    onClick={() => toast.dismiss(id)}
                    className="flex items-start gap-3 max-w-sm w-full p-3 rounded-2xl bg-[#0e0d18] border border-white/10 shadow-[0_20px_44px_-16px_rgba(0,0,0,0.7)] hover:bg-white/[0.04] transition-colors"
                  >
                    <span className="shrink-0 mt-0.5">{iconFor(n.type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate">{n.title}</p>
                      <p className="text-white/60 text-xs mt-0.5 line-clamp-2">{n.body}</p>
                    </div>
                  </a>
                ),
                { duration: 8000, position: "bottom-right" }
              );
              seen.add(n.notification_id);
            });
            if (toToast.length) writeToastedIds(seen);
          } else {
            // First load — record everything currently visible as
            // already-toasted so we don't spam on page mount.
            const seen = readToastedIds();
            fresh.forEach((n) => n.notification_id && seen.add(n.notification_id));
            writeToastedIds(seen);
          }
          firstLoad = false;
        })
        .catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    return () => { mounted = false; clearInterval(t); };
  }, [user]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/plyx/me/notifications/read-all");
      setUnread(0);
      setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
    } catch { /* silent */ }
  };

  const markOneRead = async (id) => {
    try {
      await api.post(`/plyx/me/notifications/${id}/read`);
      setItems((arr) => arr.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent */ }
  };

  if (!user) return null;

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        data-testid="plyx-notif-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors text-white"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="plyx-notif-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3D7F] text-white text-[10px] font-black grid place-items-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="plyx-notif-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[420px] overflow-y-auto rounded-2xl bg-[#0e0d18] border border-white/10 shadow-[0_24px_48px_-16px_rgba(0,0,0,0.7)] z-50"
        >
          <div className="sticky top-0 flex items-center justify-between px-4 py-3 bg-[#0e0d18] border-b border-white/10">
            <h4 className="text-white font-black text-sm">Notifications</h4>
            {unread > 0 && (
              <button
                type="button"
                data-testid="plyx-notif-mark-all-read"
                onClick={markAllRead}
                className="text-[10px] font-bold text-[#FFCC2E] hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-white/40 text-sm">You&apos;re all caught up.</p>
          ) : (
            <ul>
              {items.map((n) => (
                <li
                  key={n.notification_id}
                  data-testid={`plyx-notif-${n.notification_id}`}
                  className={`border-b border-white/5 last:border-b-0 ${n.is_read ? "opacity-60" : "bg-white/[0.03]"}`}
                >
                  <Link
                    to={n.link || "#"}
                    onClick={() => { if (!n.is_read) markOneRead(n.notification_id); setOpen(false); }}
                    className="block px-4 py-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      {!n.is_read && (
                        <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFCC2E]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-bold truncate">{n.title}</p>
                        <p className="text-white/60 text-xs mt-0.5 line-clamp-2">{n.body}</p>
                        <p className="text-white/30 text-[10px] mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default PlyxNotificationBell;
